import React, { useState, useEffect } from 'react';
import './EditCatecheseItemModal.css';
import {
  etappeOptions,
  bisdomOptions,
  getParochiesForBisdom,
  getNextEtappe
} from '../config/formOptions';
import {
  getGemeenschappenForParochieDynamic,
  getGemeenschapByLocation
} from '../services/gemeenschappen';

const EMPTY_FORM_DATA = {
  etappe: '',
  datum: '',
  bisdom: '',
  parochie: '',
  gemeenschap: '',
  nieuweGemeenschapNaam: '',
  catechistVoornaam: '',
  catechistAchternaam: '',
  catechistEmail: ''
};

const NIEUWE_GEMEENSCHAP_VALUE = '__nieuwe_gemeenschap__';

/**
 * EditCatecheseItemModal Component
 * 
 * A modal dialog for adding new or editing existing catechism items
 * Includes form validation for required fields (etappe and datum)
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSubmit - Callback when form is submitted with valid data
 * @param {boolean} isEditMode - Whether the modal is in edit mode
 * @param {Object} initialData - Initial data for edit mode
 * @param {Function} onDelete - Callback when delete button is clicked (edit mode only)
 */
const EditCatecheseItemModal = ({ isOpen, onClose, onSubmit, isEditMode = false, initialData = null, onDelete }) => {
  // Form data state
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  // Form validation errors state
  const [errors, setErrors] = useState({});

  // Options for cascading dropdowns
  const [availableParochies, setAvailableParochies] = useState([]);
  const [availableGemeenschappen, setAvailableGemeenschappen] = useState([]);
  const [suggestedEtappe, setSuggestedEtappe] = useState('');
  const [isEtappeManuallyEdited, setIsEtappeManuallyEdited] = useState(false);
  const [selectedGemeenschapData, setSelectedGemeenschapData] = useState(null);

  const getFullName = (voornaam, achternaam, fallback = '') => {
    const fullName = [voornaam, achternaam]
      .map((part) => (part || '').trim())
      .filter(Boolean)
      .join(' ');

    return fullName || (fallback || '').trim();
  };

  const isNieuweGemeenschap = formData.gemeenschap === NIEUWE_GEMEENSCHAP_VALUE;

  const getGemeenschapOptions = async (bisdom, parochie) => {
    if (!bisdom || !parochie) return [];

    try {
      const dynamicGemeenschappen = await getGemeenschappenForParochieDynamic(bisdom, parochie);
      return dynamicGemeenschappen;
    } catch (error) {
      console.error('Error loading dynamic gemeenschap options:', error);
      return [];
    }
  };

  /**
   * Format a date/timestamp to a readable Dutch date and time string
   */
  const formatDateTime = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Populate form with initial data when in edit mode
   */
  useEffect(() => {
    if (!isOpen) return;

    let isActive = true;

    if (!isEditMode || !initialData) {
      setFormData(EMPTY_FORM_DATA);
      setAvailableParochies([]);
      setAvailableGemeenschappen([]);
      setSelectedGemeenschapData(null);
      setSuggestedEtappe('');
      setIsEtappeManuallyEdited(false);
      setErrors({});
      return;
    }

    const initializeEditMode = async () => {
      setFormData({
        etappe: initialData.etappe || '',
        datum: initialData.datum || '',
        bisdom: initialData.bisdom || '',
        parochie: initialData.parochie || '',
        gemeenschap: initialData.gemeenschap || '',
        nieuweGemeenschapNaam: '',
        catechistVoornaam: initialData.catechistVoornaam || '',
        catechistAchternaam: initialData.catechistAchternaam || '',
        catechistEmail: initialData.catechistEmail || ''
      });

      if (initialData.bisdom) {
        const parochies = getParochiesForBisdom(initialData.bisdom);
        if (!isActive) return;
        setAvailableParochies(parochies);

        if (initialData.parochie) {
          const gemeenschappen = await getGemeenschapOptions(initialData.bisdom, initialData.parochie);
          if (!isActive) return;
          setAvailableGemeenschappen(gemeenschappen);

          if (initialData.gemeenschap) {
            try {
              const selectedGemeenschap = await getGemeenschapByLocation(
                initialData.bisdom,
                initialData.parochie,
                initialData.gemeenschap
              );
              if (!isActive) return;
              setSelectedGemeenschapData(selectedGemeenschap || null);
            } catch (error) {
              console.error('Error loading selected gemeenschap details:', error);
              if (!isActive) return;
              setSelectedGemeenschapData(null);
            }
          }
        }
      }

      setSuggestedEtappe('');
      setIsEtappeManuallyEdited(false);
      setErrors({});
    };

    initializeEditMode();

    return () => {
      isActive = false;
    };
  }, [isEditMode, initialData, isOpen]);

  /**
   * Handles input changes and clears validation errors for the changed field
   * Also handles cascading dropdown logic
   */
  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'etappe') {
      setIsEtappeManuallyEdited(true);
      setFormData(prev => ({
        ...prev,
        etappe: value
      }));
    }
    
    // Handle cascading logic for Bisdom selection
    else if (name === 'bisdom') {
      const parochies = getParochiesForBisdom(value);
      setAvailableParochies(parochies);
      setAvailableGemeenschappen([]);
      setSuggestedEtappe('');
      setSelectedGemeenschapData(null);
      setFormData(prev => ({
        ...prev,
        bisdom: value,
        parochie: '', // Reset parochie when bisdom changes
        gemeenschap: '', // Reset gemeenschap when bisdom changes
        nieuweGemeenschapNaam: '',
        catechistVoornaam: '',
        catechistAchternaam: '',
        catechistEmail: ''
      }));
    }
    // Handle cascading logic for Parochie selection
    else if (name === 'parochie') {
      const selectedBisdom = formData.bisdom || e.target.form?.elements?.bisdom?.value || '';
      const gemeenschappen = await getGemeenschapOptions(selectedBisdom, value);
      setAvailableGemeenschappen(gemeenschappen);
      setSuggestedEtappe('');
      setSelectedGemeenschapData(null);
      setFormData(prev => ({
        ...prev,
        parochie: value,
        gemeenschap: '', // Reset gemeenschap when parochie changes
        nieuweGemeenschapNaam: '',
        catechistVoornaam: '',
        catechistAchternaam: '',
        catechistEmail: ''
      }));
    }
    else if (name === 'gemeenschap') {
      const selectedBisdom = formData.bisdom || e.target.form?.elements?.bisdom?.value || '';
      const selectedParochie = formData.parochie || e.target.form?.elements?.parochie?.value || '';

      if (value === NIEUWE_GEMEENSCHAP_VALUE) {
        setSelectedGemeenschapData(null);
        setSuggestedEtappe('');

        setFormData(prev => ({
          ...prev,
          gemeenschap: value,
          catechistVoornaam: '',
          catechistAchternaam: '',
          catechistEmail: ''
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        gemeenschap: value
      }));

      let nextEtappe = '';
      try {
        const selectedGemeenschap = await getGemeenschapByLocation(selectedBisdom, selectedParochie, value);
        setSelectedGemeenschapData(selectedGemeenschap || null);
        const autoCatechistName = getFullName(
          selectedGemeenschap?.catechistVoornaam,
          selectedGemeenschap?.catechistAchternaam
        );
        const autoCatechistEmail = (selectedGemeenschap?.catechistEmail || '').trim();

        setFormData(prev => ({
          ...prev,
          catechistVoornaam: selectedGemeenschap?.catechistVoornaam || autoCatechistName,
          catechistAchternaam: selectedGemeenschap?.catechistAchternaam || '',
          catechistEmail: autoCatechistEmail
        }));

        nextEtappe = getNextEtappe(selectedGemeenschap?.laatsteEtappe || '');
      } catch (error) {
        console.error('Error calculating suggested etappe:', error);
        setSelectedGemeenschapData(null);
      }

      setSuggestedEtappe(nextEtappe);

      if (nextEtappe && !isEditMode && !isEtappeManuallyEdited) {
        setFormData(prev => {
          const canAutoFill = !prev.etappe || prev.etappe === suggestedEtappe;
          if (!canAutoFill) return prev;
          return {
            ...prev,
            etappe: nextEtappe
          };
        });
      }
    }
    // Handle all other fields normally
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validates the form data
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validate = () => {
    const newErrors = {};
    
    // Check required fields
    if (!formData.etappe.trim()) {
      newErrors.etappe = 'De etappe is verplicht';
    }
    
    if (!formData.datum) {
      newErrors.datum = 'De datum is verplicht';
    }

    if (!formData.bisdom) {
      newErrors.bisdom = 'Het bisdom is verplicht';
    }

    if (!formData.parochie) {
      newErrors.parochie = 'De parochie is verplicht';
    }

    if (!formData.gemeenschap) {
      newErrors.gemeenschap = 'De gemeenschap is verplicht';
    }

    if (formData.gemeenschap === NIEUWE_GEMEENSCHAP_VALUE) {
      if (!formData.nieuweGemeenschapNaam.trim()) {
        newErrors.nieuweGemeenschapNaam = 'De naam van de nieuwe gemeenschap is verplicht';
      }

      if (!formData.catechistVoornaam.trim()) {
        newErrors.catechistVoornaam = 'Voornaam van de catechist is verplicht';
      }

      if (!formData.catechistAchternaam.trim()) {
        newErrors.catechistAchternaam = 'Achternaam van de catechist is verplicht';
      }

      if (!formData.catechistEmail.trim()) {
        newErrors.catechistEmail = 'Email van de catechist is verplicht';
      }
    } else {
      if (!formData.catechistVoornaam.trim()) {
        newErrors.catechistVoornaam = 'Voornaam van de catechist ontbreekt voor deze gemeenschap';
      }

      if (!formData.catechistAchternaam.trim()) {
        newErrors.catechistAchternaam = 'Achternaam van de catechist ontbreekt voor deze gemeenschap';
      }

      if (!formData.catechistEmail.trim()) {
        newErrors.catechistEmail = 'Email van de catechist ontbreekt voor deze gemeenschap';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission
   * Validates data and calls onSubmit callback if valid
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const trimmedVoornaam = formData.catechistVoornaam.trim();
    const trimmedAchternaam = formData.catechistAchternaam.trim();
    const isNewGemeenschap = formData.gemeenschap === NIEUWE_GEMEENSCHAP_VALUE;
    const finalGemeenschapName = isNewGemeenschap ? formData.nieuweGemeenschapNaam.trim() : formData.gemeenschap;

    onSubmit({
      ...formData,
      gemeenschap: finalGemeenschapName,
      isNieuweGemeenschap: isNewGemeenschap,
      catechistVoornaam: trimmedVoornaam || selectedGemeenschapData?.catechistVoornaam || '',
      catechistAchternaam: trimmedAchternaam || selectedGemeenschapData?.catechistAchternaam || '',
      catechistEmail: formData.catechistEmail.trim() || (selectedGemeenschapData?.catechistEmail || '').trim()
    });
    
    // Reset form
    setFormData(EMPTY_FORM_DATA);
    setErrors({});
    setAvailableParochies([]);
    setAvailableGemeenschappen([]);
    setSuggestedEtappe('');
    setIsEtappeManuallyEdited(false);
  };

  /**
   * Handles modal close
   * Resets form data and errors before closing
   */
  const handleClose = () => {
    setFormData(EMPTY_FORM_DATA);
    setErrors({});
    setAvailableParochies([]);
    setAvailableGemeenschappen([]);
    setSuggestedEtappe('');
    setIsEtappeManuallyEdited(false);
    onClose();
  };

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Element bewerken' : 'Nieuw element toevoegen'}</h2>
          <button className="close-button" onClick={handleClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="bisdom">
              Bisdom <span className="required">*</span>
            </label>
            <select
              id="bisdom"
              name="bisdom"
              value={formData.bisdom}
              onChange={handleChange}
              className={errors.bisdom ? 'error' : ''}
            >
              <option value="">Selecteer een bisdom</option>
              {bisdomOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.bisdom && <span className="error-message">{errors.bisdom}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="parochie">
              Parochie <span className="required">*</span>
            </label>
            <select
              id="parochie"
              name="parochie"
              value={formData.parochie}
              onChange={handleChange}
              disabled={!formData.bisdom}
              className={errors.parochie ? 'error' : ''}
            >
              <option value="">{!formData.bisdom ? 'Selecteer eerst een bisdom' : 'Selecteer een parochie'}</option>
              {availableParochies.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.parochie && <span className="error-message">{errors.parochie}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="gemeenschap">
              Gemeenschap <span className="required">*</span>
            </label>
            <select
              id="gemeenschap"
              name="gemeenschap"
              value={formData.gemeenschap}
              onChange={handleChange}
              disabled={!formData.parochie}
              className={errors.gemeenschap ? 'error' : ''}
            >
              <option value="">{!formData.parochie ? 'Selecteer eerst een parochie' : 'Selecteer een gemeenschap'}</option>
              {availableGemeenschappen.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
              {!isEditMode && formData.parochie && (
                <option value={NIEUWE_GEMEENSCHAP_VALUE}>Nieuwe gemeenschap</option>
              )}
            </select>
            {!isEditMode && formData.parochie && availableGemeenschappen.length === 0 && (
              <span className="helper-message">
                Geen bestaande gemeenschap gevonden voor deze parochie. Kies "Nieuwe gemeenschap" om er een toe te voegen.
              </span>
            )}
            {errors.gemeenschap && <span className="error-message">{errors.gemeenschap}</span>}
          </div>

          {isNieuweGemeenschap && (
            <>
              <div className="form-group">
                <label htmlFor="nieuweGemeenschapNaam">
                  Naam nieuwe gemeenschap <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="nieuweGemeenschapNaam"
                  name="nieuweGemeenschapNaam"
                  value={formData.nieuweGemeenschapNaam}
                  onChange={handleChange}
                  placeholder="Voer de naam van de nieuwe gemeenschap in"
                  className={errors.nieuweGemeenschapNaam ? 'error' : ''}
                />
                {errors.nieuweGemeenschapNaam && <span className="error-message">{errors.nieuweGemeenschapNaam}</span>}
              </div>

              <div className="catechist-group">
                <span className="group-label">Catechist</span>
                <div className="name-row">
                  <div className="form-group">
                    <label htmlFor="catechistVoornaam">
                      Voornaam <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="catechistVoornaam"
                      name="catechistVoornaam"
                      value={formData.catechistVoornaam}
                      onChange={handleChange}
                      placeholder="Voer de voornaam van de catechist in"
                      className={errors.catechistVoornaam ? 'error' : ''}
                    />
                    {errors.catechistVoornaam && <span className="error-message">{errors.catechistVoornaam}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="catechistAchternaam">
                      Achternaam <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="catechistAchternaam"
                      name="catechistAchternaam"
                      value={formData.catechistAchternaam}
                      onChange={handleChange}
                      placeholder="Voer de achternaam van de catechist in"
                      className={errors.catechistAchternaam ? 'error' : ''}
                    />
                    {errors.catechistAchternaam && <span className="error-message">{errors.catechistAchternaam}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="catechistEmail">
                    Catechist email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="catechistEmail"
                    name="catechistEmail"
                    value={formData.catechistEmail}
                    onChange={handleChange}
                    placeholder="Voer het email adres van de catechist in"
                    className={errors.catechistEmail ? 'error' : ''}
                  />
                  {errors.catechistEmail && <span className="error-message">{errors.catechistEmail}</span>}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="etappe">
              Etappe <span className="required">*</span>
            </label>
            <select
              id="etappe"
              name="etappe"
              value={formData.etappe}
              onChange={handleChange}
              className={errors.etappe ? 'error' : ''}
            >
              <option value="">Selecteer een etappe</option>
              {etappeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {suggestedEtappe && (
              <span className="suggestion-message">
                Volgende etappe voor deze gemeenschap: {suggestedEtappe}
              </span>
            )}
            {errors.etappe && <span className="error-message">{errors.etappe}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="datum">
              Datum <span className="required">*</span>
            </label>
            <input
              type="date"
              id="datum"
              name="datum"
              value={formData.datum}
              onChange={handleChange}
              className={errors.datum ? 'error' : ''}
            />
            {errors.datum && <span className="error-message">{errors.datum}</span>}
          </div>

          {!isNieuweGemeenschap && (
            <>
              <div className="catechist-group">
                <span className="group-label">Catechist</span>
                <div className="name-row">
                  <div className="form-group">
                    <label htmlFor="catechistVoornaam">Voornaam</label>
                    <input
                      type="text"
                      id="catechistVoornaam"
                      name="catechistVoornaam"
                      value={formData.catechistVoornaam}
                      readOnly
                      className={errors.catechistVoornaam ? 'error' : ''}
                    />
                    {errors.catechistVoornaam && <span className="error-message">{errors.catechistVoornaam}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="catechistAchternaam">Achternaam</label>
                    <input
                      type="text"
                      id="catechistAchternaam"
                      name="catechistAchternaam"
                      value={formData.catechistAchternaam}
                      readOnly
                      className={errors.catechistAchternaam ? 'error' : ''}
                    />
                    {errors.catechistAchternaam && <span className="error-message">{errors.catechistAchternaam}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="catechistEmail">Catechist email</label>
                  <input
                    type="text"
                    id="catechistEmail"
                    name="catechistEmail"
                    value={formData.catechistEmail}
                    readOnly
                    className={errors.catechistEmail ? 'error' : ''}
                  />
                  {errors.catechistEmail && <span className="error-message">{errors.catechistEmail}</span>}
                </div>
              </div>
            </>
          )}

          {isEditMode && initialData?.updatedBy && (
            <div className="edit-info">
              Laatst bewerkt door {initialData.updatedBy} op {formatDateTime(initialData.updatedAt)}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" onClick={handleClose} className="cancel-button">
              Annuleren
            </button>
            {isEditMode ? (
              <>
                <button type="button" onClick={() => onDelete(initialData.id)} className="delete-button">
                  Verwijderen
                </button>
                <button type="submit" className="submit-button">
                  Opslaan
                </button>
              </>
            ) : (
              <button type="submit" className="submit-button">
                Toevoegen
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCatecheseItemModal;
